// APP#59 — 密碼政策動態化（per-Application，restful#76）。
// 有效政策來源優先序：
//   1) brand bootstrap 的 `password_policy`（解析後有效政策，brand.ts 注入）
//   2) 422 WEAK_PASSWORD error body 的 `policy`（server=最終權威，收到即刷新顯示）
//   3) 平台預設 {min_length:6, 字母+數字+符號}（2026-07-11 業主決策：長度 10→6 放寬、組合強制保留）
// 前端驗證僅 UX 先擋；最終權威永遠是 server。
import type { TFunction } from 'i18next';
import { ApiError } from './api';

export interface PasswordPolicy {
  min_length: number;
  require_letter: boolean;
  require_digit: boolean;
  require_symbol: boolean;
}

/** 平台預設政策（restful 00 §5.1 / restful#76）。 */
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = Object.freeze({
  min_length: 6,
  require_letter: true,
  require_digit: true,
  require_symbol: true,
});

/** 防禦性收斂：非物件→平台預設；min_length clamp 6–128（樓地板同 server）；缺欄位補平台預設值。 */
export function normalizePasswordPolicy(raw: unknown): PasswordPolicy {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return DEFAULT_PASSWORD_POLICY;
  const o = raw as Record<string, unknown>;
  const n = typeof o.min_length === 'number' && Number.isFinite(o.min_length)
    ? Math.trunc(o.min_length)
    : DEFAULT_PASSWORD_POLICY.min_length;
  return {
    min_length: Math.min(128, Math.max(6, n)),
    require_letter: typeof o.require_letter === 'boolean' ? o.require_letter : true,
    require_digit: typeof o.require_digit === 'boolean' ? o.require_digit : true,
    require_symbol: typeof o.require_symbol === 'boolean' ? o.require_symbol : true,
  };
}

/** 前端預檢（僅 UX；server 最終權威）。 */
export function passwordMeetsPolicy(pw: string, policy: PasswordPolicy): boolean {
  if (pw.length < policy.min_length) return false;
  if (policy.require_letter && !/[A-Za-z]/.test(pw)) return false;
  if (policy.require_digit && !/[0-9]/.test(pw)) return false;
  if (policy.require_symbol && !/[^A-Za-z0-9]/.test(pw)) return false;
  return true;
}

/** 組合要求的 i18n key 後綴（Letter/Digit/Symbol 依政策開關組合）；全關 → null。 */
function reqsText(policy: PasswordPolicy, t: TFunction): string | null {
  const key =
    (policy.require_letter ? 'Letter' : '') +
    (policy.require_digit ? 'Digit' : '') +
    (policy.require_symbol ? 'Symbol' : '');
  return key ? t(`pwPolicy.req${key}`) : null;
}

/** 表單提示文案，如預設政策 →「至少 6 字，需含字母、數字與符號。」 */
export function passwordPolicyHint(policy: PasswordPolicy, t: TFunction): string {
  const reqs = reqsText(policy, t);
  return reqs
    ? t('pwPolicy.hint', { min: policy.min_length, reqs })
    : t('pwPolicy.hintMinOnly', { min: policy.min_length });
}

/** 驗證失敗錯誤文案（前端預檢與 422 顯示共用）。 */
export function passwordPolicyError(policy: PasswordPolicy, t: TFunction): string {
  const reqs = reqsText(policy, t);
  return reqs
    ? t('pwPolicy.weak', { min: policy.min_length, reqs })
    : t('pwPolicy.weakMinOnly', { min: policy.min_length });
}

/** 從 422 WEAK_PASSWORD 的 error body 取 server 政策（server=最終權威）；非該錯誤或無 policy → null。 */
export function policyFromApiError(err: unknown): PasswordPolicy | null {
  if (!(err instanceof ApiError) || err.code !== 'WEAK_PASSWORD') return null;
  const p = err.details?.policy;
  return p && typeof p === 'object' ? normalizePasswordPolicy(p) : null;
}
